/** @jsxImportSource @emotion/react */
import { FIELD } from "../../../../actions/Layers/fields";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { layerFormFields } from "../../../../actions/Tenants/config/layerFormFields";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import MetaForm from "../../../Forms/MetaForm";
import { FORMATTERS } from "../../Layers/fields/formatters";
import CategoryConfigForm from "./CategoryConfigForm";
import { flagMethods } from "./ColorCodeMetricForm";
import FilterableConfigList, { FilterableConfigListPropsType } from "./FilterableConfigList";
import { yesOrNoRadio } from "./util";

export const FOOTER_TYPES = {
    average: "average",
    median: "median",
    sum: "sum",
    count: "count",
    range: "range",
    mode: "mode",

};

const defaultField = {
    field: {
        field_id: null,
        fieldname_getter: "",
        label: "",
        value_if_null: "-",
    },
    display_filter: {
        fruit_types: [],
        exclude_fruit_types: [],
        layer_types: [],
    }
};

// const formatOptionLabel = (props) => {
//     const { label, fieldname_getter, field_type, append_key } = props;
//     if (!label) {
//         return <span className="text-muted">Select field type</span>;
//     }
//     return (
//         <div className="py-1">
//             <div><b>{label}</b></div>
//             <div>
//                 <Badge size="sm" className="me-1">{field_type}</Badge>
//                 {append_key && <Badge size="sm" className="me-1">{append_key}</Badge>}
//                 <Badge size="sm" className="me-1"> [ {fieldname_getter} ] </Badge>
//             </div>
//         </div>
//     );
// };

const uniqueByValueAndLabel = (arr) => {
    const seen = new Set();
    return arr.filter((item) => {
        if (seen.has(`${item.value}-${item.label}`)) return false;
        seen.add(`${item.value}-${item.label}`);
        return true;
    });
};

// The config forms write a cleared number input as `false` or "", but 0 is a
// valid bound for a ripening scale, so it must not be treated as "unset".
const acceptBound = (raw): number | null => {
    if (raw === null || raw === undefined || raw === "" || typeof raw === "boolean") return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
};

export const acceptRangeIsInverted = (object): boolean => {
    const min = acceptBound(object?.accept_min);
    const max = acceptBound(object?.accept_max);
    return min !== null && max !== null && min > max;
};

export const getAcceptRangeSummary = (object): string | null => {
    const min = acceptBound(object?.accept_min);
    const max = acceptBound(object?.accept_max);
    if (min === null && max === null) return null;
    if (min !== null && max !== null) return `accepted ${min} - ${max}`;
    if (min !== null) return `accepted >= ${min}`;
    return `accepted <= ${max}`;
};

export const getMetricMetaKeysOptions = (config) => {
    const color_codes = config.color_codes || [];
    const metricMetaKeys = color_codes.flatMap((i) => (i.metrics || []).flatMap((j) => [
        j.metric_meta_key && {
            label: `(Metric) ${j.metric_meta_key}`,
            value: j.metric_meta_key,
            type: "metric",
        },
        (j.metric_meta_key && j.relative_absolute === "relative")
        && {
            label: `(Metric) ${j.metric_meta_key}_relative`,
            type: "metric",
            value: `${j.metric_meta_key}_relative`,
        },
        (j.flag_method === flagMethods.flag_check__check_field && j.relative_absolute === "relative")
        && {
            label: `(Metric) ${j.field}_relative`,
            type: "metric",
            value: `${j.field}_relative`,
        },
        j.flag_name && {
            label: `(Flag) ${j.flag_name}`,
            type: "flag",
            value: j.flag_name,
        },
        j.category_name && {
            label: `(Category) ${j.category_name}`,
            type: "category",
            value: j.category_name,
        },
        j.category_name && {
            label: `(Category) ${j.category_name}_color`,
            type: "category_color",
            value: `${j.category_name}_color`,
        }
    ])).filter((i) => i);
    return metricMetaKeys;
};

const fieldFormHook = (config) => {

    const metricMetaKeysOptions = getMetricMetaKeysOptions(config);

    // * All other forms
    const allOtherForms = getFilledArrayOrDefault(config.configs).map((i) => i.config)
        .flatMap((i) => Object.entries(i)
            .filter((i) => isFilledArray(i[1]))
            .flatMap(([key, fields]) => getFilledArrayOrDefault(fields)
                .map((i) => i.field || i)
                .filter((i) => i.name)
                .map((i) => ({ label: `(${key}) ${i.name}`, original_label: i.label || i.name, value: i.name }))));


    const layerFieldNameOptions = layerFormFields.map((i) => ({ value: i.name, label: `(layer) ${i.label}` }));

    const checkLocations = (config.check_locations || []).map((i) => ({ label: `${i.text} [${i.value}]`, value: i.value }));

    const fieldname_getter_options = uniqueByValueAndLabel([
        ...layerFieldNameOptions,
        ...metricMetaKeysOptions,
        // ...copiedFromCheckFieldNames,
        // ...fruitFieldNames,
        // ...copiedFromRoomConditionsFieldNames,
        // ...copiedFromRoomMetaFieldNames,
        ...allOtherForms,
    ]);

    const fieldname_getter_callback = (object, setObject) => ({ field, value }) => {
        const fieldname_getter = (object.fieldname_getter_location_prefix ? `${object.fieldname_getter_location_prefix}_` : "") + value;
        setObject({ ...object, [field]: value, fieldname_getter });
    };

    const getForm = (object, setObject) => [
        {
            label: "field_id",
            name: "field_id",
            description: "Which field do you want to use as a template?",
            type: "single-select",
            // formatOptionLabel,
            onchange_callback: ({ field, value }) => {
                const the_field = FIELD[value];
                if (the_field) {
                    setObject({ ...object, [field]: value, label: object.label || the_field.label, fieldname_getter: object.fieldname_getter || the_field.fieldname_getter, error: false });
                } else {
                    setObject({ ...object, [field]: value });
                }
            },
            options: Object.keys(FIELD).map((i) => ({ value: i, label: i })),
        },
        {
            label: "fieldname_getter",
            name: "fieldname_getter_common_field",
            description: "Configured and other Common platform fields, if your field is not in the list then ask a developer to add it.",
            type: "single-select",
            options: fieldname_getter_options,
            onchange_callback: fieldname_getter_callback(object, setObject)
        },
        // TODO: only show this option when working with CUSTOM_LAYER_META or similar field reading from layer
        {
            label: "Location prefix",
            name: "fieldname_getter_location_prefix",
            description: "When reading a metric or check data from layer meta, you can read from a specific location or latest registered.",
            type: "single-select",
            options: [{ label: "Latest", value: null }, ...checkLocations],
            onchange_callback: ({ field, value }) => {
                const fieldname_getter = (value ? `${value}_` : "") + object.fieldname_getter_common_field;
                setObject({ ...object, [field]: value, fieldname_getter });
            },
        },
        {
            label: "fieldname_getter",
            name: "fieldname_getter",
            description: "From which key should this field try to read data? This is a composite of the settings above, but you can adjust it here to your likings.",
            type: "text",
        },
        {
            label: "Label",
            name: "label",
            description: "What is the label of this field?",
            type: "text",

        },
        {
            label: "Options name",
            name: "options_name",
            type: "text",
            description: `Fill this in if this field has managed options that are not managed under ${object?.fieldname_getter}. leave blank to use the same as ${object?.fieldname_getter}`,
        },
        {
            label: "Formatter",
            type: "header",
        },
        {
            label: "Apply formatter",
            name: "formatter",
            description: "Apply a formatter to this field? Will also defined how this field is filterd.",
            type: "single-select",
            options: Object.keys(FORMATTERS).map((i) => ({ value: i, label: i })),
        },
        object?.formatter && {
            label: "unity",
            type: "text",
            description: "Unity for this input, e.g. KG, LBS or %.",
            name: "unity",
        },
        object?.formatter && {
            label: "Decimals",
            type: "number",
            description: "How many decimals should this field have? Use in combination with formatter.",
            name: "decimals",
        },
        object?.formatter && {
            label: "Value if null",
            type: "text",
            description: "What should be displayed if the value is null?",
            name: "value_if_null",
        },
        object?.formatter && {
            label: "Overwrite datetime format",
            type: "text",
            name: "datetime_format",
            description: "How should the date be formatted? Leave blank to use `DD-MM-YYYY` for dates and `HH:mm DD-MM-YYYY` for datetimes.",
        },
        object.formatter && object.formatter === "listToString" && {
            label: "Separator",
            type: "text",
            name: "separator",
            description: "What should be used as separator for this list?",
        },
        {
            header: "Color code",
            type: "header",
        },
        {
            label: "Flag name to color code",
            name: "base_color_code_field",
            type: "single-select",
            description: "Which field should be used to color code this field?",
            options: metricMetaKeysOptions.filter((i) => i.type === "flag"),
            onchange_callback: ({ field, value }) => {
                const color_code_field = (object.color_code_field_location_prefix ? `${object.color_code_field_location_prefix}_` : "") + value;
                setObject({ ...object, [field]: value, color_code_field });
            },
        },
        {
            label: "Location prefix",
            name: "color_code_field_location_prefix",
            description: `When reading a flag to use for color code, you can read from a specific location or latest registered. ${object?.color_code_field ? `Will read from '${object.color_code_field}'` : ""}`,
            type: "single-select",
            options: [{ label: "Latest", value: null }, ...checkLocations],
            onchange_callback: ({ field, value }) => {
                const color_code_field = (value ? `${value}_` : "") + object.base_color_code_field;
                setObject({ ...object, [field]: value, color_code_field });
            },
        },
        {
            label: "Ripening field",
            type: "header",
        },
        {
            label: "Reverse Ripening Axis?",
            name: "higher_value_is_more_ripe",
            description: "Is the ripening axis reversed? Default is no. (e.g. Firmness is lower when ripe)",
            type: "radio",
            options: [
                { label: "No, lower value is more ripe (e.g. Firmness)", value: false },
                { label: "Yes, higher value is more ripe (e.g. Maturity)", value: true }
            ]
        },
        {
            label: "Default log scale?",
            name: "default_log_scale",
            description: "Should this field be displayed in log scale by default?",
            ...yesOrNoRadio
        },
        {
            label: "Category name",
            type: "text",
            description: <span>Category name e.g. firmness_class, ripening_class, color_class etc. This field will try to read the category from `dominant_{object.category_name}` and `{object.category_name}` in `check_meta` and/or `fruit_meta`.</span>,
            name: "category_name",
        },
        {
            label: "Accepted range (rejection)",
            type: "header",
        },
        {
            label: "Accepted range - min",
            name: "accept_min",
            type: "number",
            lg: 6,
            description: "Reject the pallet at the external service when the measured value is below this. Leave empty for no lower bound. E.g. avocado firmness 0.6 - anything softer is too ripe and gets rejected.",
        },
        {
            label: "Accepted range - max",
            name: "accept_max",
            type: "number",
            lg: 6,
            description: acceptRangeIsInverted(object)
                ? "Max must be greater than min - the accepted range is currently empty, so every measurement would be rejected."
                : "Reject the pallet at the external service when the measured value is above this. Leave empty for no upper bound. E.g. banana colour 3.5 - anything riper gets rejected.",
        },
        {
            label: "Inherit ripening classes from fruit config",
            name: "inherit_ripening_classes_from_fruit_config",
            type: "single-select",
            options: config.get_fruit_types().flatMap((fruitType) => fruitType.ripening_fields.map((ripeningField) => ({ label: `Classes from ${fruitType.text} - ${ripeningField.label} [${ripeningField.fieldname_getter}]`, value: ripeningField.fieldname_getter }))),
        },
        {
            label: "Variety condition (fruit_variety)",
            name: "fruit_variety_csv",
            type: "text",
            default_value: (object.conditions?.fruit_variety || []).join(", "),
            description: "Comma-separated fruit_variety values (e.g. kent, keitt) this ripening field applies to. Leave empty to apply to all varieties. Add multiple ripening fields with the same fieldname_getter but different variety conditions to give a fruit per-variety category ranges (e.g. mango Kent vs Keitt).",
            onchange_callback: ({ value }) => {
                const values = String(value || "").split(",").map((x) => x.trim()).filter(Boolean);
                const next = {
                    ...object,
                    conditions: { ...(object.conditions || {}), fruit_variety: values.length ? values : undefined },
                };
                // drop the UI-only CSV helper key so it is never persisted
                delete next.fruit_variety_csv;
                setObject(next);
            },
        },
        {
            type: "element",
            el: <CategoryConfigForm
                fields={object.categories || []}
                description="Categories for this field, used for on the fly color coding and swimming lanes in ripening graphs, displaying badges."
                name="categories"
                useDisplayFilter={false}
                title="Categories"
                setArray={(categories) => setObject({ ...object, categories })} />
        },
        {
            label: "Defects",
            type: "header",
        },
        {
            label: "Defect position",
            name: "defect_position",
            type: "single-select",
            return_array: true,
            multi: true,
            options: config.get_defect_positions().options,
        },
        {
            label: "Defect Severity",
            name: "defect_severity",
            type: "single-select",
            return_array: true,
            multi: true,
            advanced: false,
            options: config.get_defect_severities().options,
        },
        {
            label: "Other",
            type: "header",
        },
        {
            label: "Exclude from download",
            name: "excluded_from_download",
            description: "Exclude this field from download?",
            ...yesOrNoRadio
        },
        {
            label: "PDF column width weight",
            name: "pdf_column_width_weight",
            description: "Relative width of this field in PDF pallet tables. Leave empty for the default weight of 1.",
            type: "number",
            min: 0.1,
        },
        {
            label: "Footer",
            name: "footer",
            description: "Add a footer to this field?",
            type: "single-select",
            options: Object.keys(FOOTER_TYPES).map((i) => ({ label: i, value: i })),
        },
    ].filter((i) => i);
    return getForm;
};


export function SingleFieldForm({ setField, field, title, }) {
    const config = useConfig();
    const getFieldForm = fieldFormHook(config);

    const header = {
        label: title,
        type: "header"
    };

    return <MetaForm
        meta={[header, ...getFieldForm(field, setField)]}
        setValue={(field_name, value) => setField({ ...field, [field_name]: value })}
        object={field}
        config={config}
    />;
}

type FieldConfigFormProps = Pick<FilterableConfigListPropsType, "name" | "title" | "description" | "setArray" | "fields" | "useDisplayFilter">;

export default function FieldConfigForm({ name, title, fields, description, setArray, useDisplayFilter = ["fruit_types", "exclude_fruit_types", "layer_types", "manual_or_with_mini", "meta_conditions"] }: FieldConfigFormProps) {
    const getFieldTitle = (object) => {
        const acceptRange = getAcceptRangeSummary(object);
        return (
            <span>
                {object.label} [{object.fieldname_getter}] - {object.field_id}
                {acceptRange && <span className="text-muted small ms-2">· {acceptRange}</span>}
            </span>
        );
    };
    const config = useConfig();
    const getFieldForm = fieldFormHook(config);

    const fields_checked_for_error = fields.map((i) => ({ ...i, error: !FIELD[i.field?.field_id] }));
    const has_undefined_field = fields_checked_for_error.some((i) => i.error);
    const error = has_undefined_field ? "Some fields are using deprecated fields or are not properly defined." : false;

    return <FilterableConfigList
        name={name}
        useDisplayFilter={useDisplayFilter}
        title={title}
        description={description}
        fields={fields_checked_for_error}
        setArray={setArray}
        defaultField={defaultField}
        getFieldForm={getFieldForm}
        error={error}
        getFieldTitle={getFieldTitle} />;

}


